import { Route } from '@angular/router';
import { AuthUrlParts, maintenanceGuard, outOfMaintenanceGuard, redirectToGuard } from '@examdojo/auth';
import { billingInfoResolver } from '@examdojo/billing';
import {
  syllabusUserSubscriptionsResolver,
  topicLevel2ProficienciesResolver,
  topicsTreeResolver,
} from '@examdojo/category/v2';
import { dismissDialogIfOpen } from '@examdojo/core/dialog';
import { featureFlagGuard } from '@examdojo/core/feature-flag';
import { NotFoundComponent } from '@examdojo/core/not-found';
import { ExamDojoFeatureFlag } from '@examdojo/models/feature-flag';
import { AuthGuard, userRoleResolver } from '@examdojo/user';
import { userSettingsResolver } from '@examdojo/user-settings';
import { activityResolver } from './activity/activity.resolver';
import { RootUrlParts } from './app.model';
import { authRoutes } from './auth/auth.routes';
import { internalUserGuard } from './auth/internal-user.guard';
import { AssessmentFormDialogService } from './features/assessments/assessment-form-dialog/assessment-form-dialog.service';
import { ChallengeDialogService } from './features/exams/dialog/challenge-dialog.service';
import { ChallengeReviewDialogService } from './features/exams/review-dialog/challenge-review-dialog.service';
import { HomeworkActivityDialogService } from './features/homework/dialog/homework-activity-dialog.service';
import { LEARN_HUB_ROUTES } from './features/learn-hub/learn-hub.routes';
import { TopicPracticeDialogService } from './features/practice/topic-practice/dialog/topic-practice-dialog.service';
import { QuestionBankAnswerReviewDialogService } from './features/question-bank/pages/question-bank-answer-review/question-bank-answer-review-dialog.service';
import { forceJoinClassGuard } from './features/classes/join-class/force-join-class.guard';
import { onboardingGuard } from './onboarding/onboarding.guard';
import { onboardingRoutes } from './onboarding/onboarding.routes';
import { RouteTransition, SlideRelativesTransition } from './route-transition';
import { ErrorPageComponent } from './shared/error-page.component';
import { MaintenancePageComponent } from './shared/maintenance-page.component';
import { userProgramSyllabusesResolver } from './shared/syllabus.resolver';
import { ShellComponent } from './shell/shell.component';
import { userMarksResolver } from './user/mark/user-marks.resolver';

export const SLUG_PARAM = 'slug';

export const appRoutes: Route[] = [
  ...authRoutes,
  ...onboardingRoutes,
  {
    path: RootUrlParts.JoinClass,
    canActivate: [maintenanceGuard, featureFlagGuard(ExamDojoFeatureFlag.Classes, { redirectTo: RootUrlParts.TopicPractice })],
    loadComponent: () =>
      import('./features/classes/join-class/join-class-page/join-class-page.component').then(
        (m) => m.JoinClassPageComponent,
      ),
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [maintenanceGuard, AuthGuard, redirectToGuard, forceJoinClassGuard, onboardingGuard],
    providers: [],
    resolve: {
      userRole: userRoleResolver,
      topicsTree: topicsTreeResolver,
      syllabuses: userProgramSyllabusesResolver,
      syllabusUserSubscriptions: syllabusUserSubscriptionsResolver,
      billing: billingInfoResolver,
      userSettings: userSettingsResolver,
      topicLevel2ProficienciesResolver: topicLevel2ProficienciesResolver,
      userMarksResolver: userMarksResolver,
      activity: activityResolver,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: RootUrlParts.TopicPractice,
      },
      {
        path: RootUrlParts.Exams,
        loadComponent: () => import('./features/exams/exams.component').then((m) => m.ExamsComponent),
        data: { compactView: false },
        canDeactivate: [dismissDialogIfOpen(ChallengeDialogService), dismissDialogIfOpen(ChallengeReviewDialogService)],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/exams/assignment/assignment.component').then((m) => m.AssignmentComponent),
            data: {
              transition: {
                name: RouteTransition.SlideRelatives,
                options: { direction: 'descendant' },
              } satisfies SlideRelativesTransition,
            },
          },
        ],
      },
      {
        path: `${RootUrlParts.StartPractice}/:questionId`,
        loadComponent: () =>
          import('./features/practice/start-practice/start-practice.component').then((m) => m.StartPracticeComponent),
        data: { compactView: true },
      },
      {
        path: RootUrlParts.TopicPractice,
        loadComponent: () => import('./features/practice/practice.component').then((m) => m.PracticeComponent),
        data: { compactView: false },
        canDeactivate: [dismissDialogIfOpen(TopicPracticeDialogService)],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/practice/topic-practice/topic-practice-v2.component').then(
                (m) => m.TopicPracticeV2Component,
              ),
            data: {
              transition: {
                name: RouteTransition.SlideRelatives,
                options: { direction: 'descendant' },
              } satisfies SlideRelativesTransition,
            },
          },
          {
            path: RootUrlParts.PredictedScoreExplanation,
            loadComponent: () =>
              import(
                './features/topic-practice-v2/predicted-score-explanation/predicted-score-explanation.component'
              ).then((m) => m.PredictedScoreExplanationComponent),
            data: { compactView: false },
          },
        ],
      },
      {
        path: RootUrlParts.Settings,
        loadChildren: () => import('./settings/settings.routes').then((m) => m.PROFILE_ROUTES),
        data: { compactView: false },
      },

      {
        path: RootUrlParts.Assessments,
        loadComponent: () => import('./features/assessments/assessments.component').then((m) => m.AssessmentsComponent),
        data: { compactView: false },
        canDeactivate: [dismissDialogIfOpen(AssessmentFormDialogService)],
      },
      {
        path: RootUrlParts.Classes,
        canActivate: [featureFlagGuard(ExamDojoFeatureFlag.Classes, { redirectTo: RootUrlParts.TopicPractice })],
        loadComponent: () => import('./features/classes/classes.component').then((m) => m.ClassesComponent),
        data: { compactView: false },
        canDeactivate: [dismissDialogIfOpen(HomeworkActivityDialogService)],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/classes/class-list/class-list.component').then((m) => m.ClassListComponent),
          },
          {
            path: ':classSlug',
            loadComponent: () =>
              import('./features/classes/class-detail/class-detail.component').then((m) => m.ClassDetailComponent),
          },
        ],
      },
      {
        path: RootUrlParts.QuestionBank,
        data: { compactView: true },
        canDeactivate: [dismissDialogIfOpen(QuestionBankAnswerReviewDialogService)],
        loadComponent: () =>
          import('./features/question-bank/pages/question-bank/question-bank.component').then(
            (m) => m.QuestionBankComponent,
          ),
      },
      ...LEARN_HUB_ROUTES,
    ],
  },
  {
    path: 'kitchen-sink',
    canActivate: [AuthGuard, internalUserGuard],
    loadComponent: () =>
      import('./features/rive-kitchen-sink/rive-kitchen-sink.component').then((m) => m.RiveKitchenSinkComponent),
  },
  {
    path: 'error',
    component: ErrorPageComponent,
  },
  {
    path: 'maintenance',
    component: MaintenancePageComponent,
    canActivate: [outOfMaintenanceGuard],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

export const ROUTES_WITH_DEFAULT_TRANSITION: string[] = [...Object.values(AuthUrlParts), RootUrlParts.Onboarding];
